import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';

/** One rendered thumbnail. `hash` is present on the deck-wide reads and absent
 *  from some partial ones, so it is optional here. */
export type Thumb = { url: string; hash?: string };

/** Thumbs by deck slug, then by slide id. */
export type ThumbsBySlug = Record<string, Record<string, Thumb> | undefined>;

/** What this hook keeps per deck out of the server's answer. */
export type DeckWarmStatus = { givenUp: string[]; coolingUntil: number | null };

/**
 * The subset of `GET /api/decks/:slug/thumbs` and `POST …/thumbs/visible` this
 * hook reads. Hand-written rather than imported from the studio's shared types:
 * `shared/client` depends on `react` and nothing else, so both web apps can use
 * it (see README.md), and the workbench cannot reach `studio/src/shared`.
 */
export type ThumbsResponse = {
  thumbs?: Record<string, Thumb>;
  givenUp?: string[];
  coolingUntil?: number | null;
  busy?: boolean;
  busyKind?: 'deck' | 'capacity';
  accepted?: string[];
};
import { INITIAL, NO_PROGRESS_MS, reducer, outstanding } from './thumbWarmQueue.js';

/** Slides one request may queue. Must not exceed the route's own cap. */
const MAX_BATCH = 24;
const POLL_MS = 800;

const keyOf = (slug: string, id: string) => `${slug}\0${id}`;
const splitKey = (key: string): [string, string] => {
  const i = key.indexOf('\0');
  return [key.slice(0, i), key.slice(i + 1)];
};

/**
 * Render the slide thumbnails a grid can actually see, and only those.
 *
 * Pair it with `useVisibleSet`: that decides what is on screen, this turns it
 * into work. One deck at a time, one request per batch (never per slide — a pass
 * launches its own Chromium and runs four pages against the deck's Vite child,
 * so per-slide requests would pay a browser launch each and lose the in-pass
 * concurrency), and every wait can end.
 *
 * `coming` is what a placeholder should shimmer on: a ghost that animates for a
 * slide nobody is working on promises an image that is not arriving.
 */
export type ThumbWarmOptions = {
  /** Keys from `useVisibleSet`, made with `thumbKey`. */
  visible: string[];
  enabled?: boolean;
  /** Thumbs the caller already has, by slug — a listing snapshot, say.
   *  Anything in here is never requested. */
  known?: ThumbsBySlug;
};

export type ThumbWarm = {
  thumbs: ThumbsBySlug;
  status: Record<string, DeckWarmStatus | undefined>;
  coming: (slug: string, id: string) => boolean;
  isStuck: (slug: string, id: string) => boolean;
  retry: (slug: string, id: string) => void;
};

export function useThumbWarm({ visible, enabled = true, known }: ThumbWarmOptions): ThumbWarm {
  const [state, dispatch] = useReducer(reducer, INITIAL);
  const [thumbs, setThumbs] = useState<ThumbsBySlug>({});
  const [status, setStatus] = useState<Record<string, DeckWarmStatus | undefined>>({});
  // Slides this hook stopped waiting for — a stall, a deck-wide backoff, or a
  // transport error. Kept apart from the server's `givenUp` because they mean
  // different things (the server tried and failed; this is "I gave up watching")
  // but a tile treats them the same: it is stuck, and it needs a way back.
  // WITHOUT this the tile is a grey square forever with no explanation, which is
  // exactly what a failed render looked like before.
  const [stuck, setStuck] = useState<Record<string, boolean>>({});
  // Bumped by `retry` to force the visible set to be re-dispatched.
  const [retryNonce, setRetryNonce] = useState(0);

  // Read through refs so a new identity can't restart a live poll.
  const knownRef = useRef(known);
  knownRef.current = known;
  const thumbsRef = useRef(thumbs);
  thumbsRef.current = thumbs;

  // Group the visible keys by deck, capped. The cap matters on a wide screen at a
  // small tile zoom, where "visible" plus the observer's look-ahead margin can be
  // most of a deck — which would be the burst this feature exists to remove.
  const bySlug = useMemo(() => {
    const out: Record<string, string[]> = {};
    for (const key of visible) {
      const [slug, id] = splitKey(key);
      if (thumbs[slug]?.[id] || knownRef.current?.[slug]?.[id]) continue; // already have it
      (out[slug] ||= []).push(id);
    }
    // `visible` arrives nearest-first (useVisibleSet ranks by distance from the
    // viewport centre), so this keeps the tiles the user is actually looking at
    // and drops the ones the observer's 200px look-ahead swept up. It used to
    // arrive sorted lexicographically, which made the cut arbitrary.
    for (const slug of Object.keys(out)) out[slug] = (out[slug] ?? []).slice(0, MAX_BATCH);
    return out;
  }, [visible, thumbs]);

  // A stable signature, so the effect fires when the WANTED SET changes rather
  // than on every render of a grid that re-renders whenever an image lands.
  const wantKey = useMemo(
    () =>
      Object.keys(bySlug)
        .sort()
        .map((s) => `${s}:${(bySlug[s] ?? []).join(',')}`)
        .join('|'),
    [bySlug],
  );

  useEffect(() => {
    if (!enabled) {
      dispatch({ type: 'clear' });
      return;
    }
    dispatch({ type: 'visible', bySlug, now: Date.now() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantKey, enabled, retryNonce]);

  // Promote the next due deck. When everything is deferred there is nothing to
  // start now, so wake when the earliest becomes due — otherwise a queue of
  // deferred decks sits until some unrelated state change re-runs this.
  useEffect(() => {
    if (!enabled || state.active || state.queue.length === 0) return undefined;
    const now = Date.now();
    const soonest = Math.min(...state.queue.map((j) => j.notBefore));
    if (soonest <= now) {
      dispatch({ type: 'start', now });
      return undefined;
    }
    const t = setTimeout(() => dispatch({ type: 'start', now: Date.now() }), soonest - now);
    return () => clearTimeout(t);
  }, [enabled, state.active, state.queue]);

  // The runner: ask for one deck's visible slides, then poll until they land.
  useEffect(() => {
    const job = state.active;
    if (!job) return undefined;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let batch: string[] = [];
    let lastLeft: number | null = null;
    let progressedAt = Date.now();

    const absorb = (res: ThumbsResponse) => {
      if (res.thumbs) setThumbs((cur) => ({ ...cur, [job.slug]: { ...(cur[job.slug] || {}), ...res.thumbs } }));
      setStatus((cur) => ({
        ...cur,
        [job.slug]: { givenUp: res.givenUp || [], coolingUntil: res.coolingUntil ?? null },
      }));
    };

    // Give up on this batch, and REMEMBER which slides were left without an
    // image so their tiles can offer a way back instead of sitting grey.
    const giveUp = (res: ThumbsResponse | null) => {
      const left = outstanding(batch, res?.thumbs ?? thumbsRef.current[job.slug], res?.givenUp);
      if (left.length) {
        setStuck((cur) => {
          const next = { ...cur };
          for (const id of left) next[keyOf(job.slug, id)] = true;
          return next;
        });
      }
      dispatch({ type: 'settle', slug: job.slug });
    };

    const poll = () => {
      fetch(`/api/decks/${job.slug}/thumbs?generate=0`, { credentials: 'same-origin' })
        .then((r) => (r.ok ? (r.json() as Promise<ThumbsResponse>) : null))
        .then((res: ThumbsResponse | null) => {
          if (stopped) return;
          if (!res) return dispatch({ type: 'settle', slug: job.slug });
          absorb(res);
          const left = outstanding(batch, res.thumbs, res.givenUp).length;
          if (!left) return dispatch({ type: 'settle', slug: job.slug });
          // The server has given up on the whole deck for now. Terminal, and it
          // is the server's job to say so — a client that keeps polling here
          // relaunches Chromium every 800ms on a deck that cannot render.
          if (res.coolingUntil && res.coolingUntil > Date.now()) return giveUp(res);
          // ANY change is progress, not just a decrease.
          if (left !== lastLeft) {
            lastLeft = left;
            progressedAt = Date.now();
          }
          if (Date.now() - progressedAt > NO_PROGRESS_MS) return giveUp(res);
          timer = setTimeout(poll, POLL_MS);
        })
        .catch(() => {
          // Whatever landed stays on screen; never strand the queue.
          if (!stopped) giveUp(null);
        });
    };

    fetch(`/api/decks/${job.slug}/thumbs/visible`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ ids: job.ids }),
    })
      .then((r) => (r.ok ? (r.json() as Promise<ThumbsResponse>) : null))
      .then((res: ThumbsResponse | null) => {
        if (stopped) return;
        if (!res) return dispatch({ type: 'settle', slug: job.slug });
        absorb(res);
        // A pass of another kind owns this deck. If it is OUR own whole-deck
        // pass ('deck') its images are the ones we want, so this is a wait
        // rather than a failure — but waiting here holds the single runner, so
        // step aside and let other decks through. If it is another deck holding
        // the global slot ('capacity'), nothing we want is being made; the
        // reducer caps that wait (MAX_DEFERS) and reports the deck as starved,
        // which turns the tiles into retryable instead of waiting forever.
        if (res.busy) return dispatch({ type: 'busy', slug: job.slug, now: Date.now(), kind: res.busyKind });
        batch = res.accepted || [];
        // Nothing was queued: every id we asked for is already cached, already
        // given up on, or not in the deck's plan at all. There is no image on the
        // way, so settle instead of polling for one.
        if (!outstanding(batch, res.thumbs, res.givenUp).length) {
          // Some of what we asked for may be here BECAUSE the server has given
          // up on it, which is a stuck tile, not a finished one.
          const dead = job.ids.filter((id) => (res.givenUp || []).includes(id));
          if (dead.length) {
            setStuck((cur) => {
              const next = { ...cur };
              for (const id of dead) next[keyOf(job.slug, id)] = true;
              return next;
            });
          }
          return dispatch({ type: 'settle', slug: job.slug });
        }
        timer = setTimeout(poll, POLL_MS);
      })
      .catch(() => {
        // Standalone dev (the workbench on :3500) has no /api proxy at all.
        if (!stopped) dispatch({ type: 'settle', slug: job.slug });
      });

    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [state.active]);

  // A deck the queue stopped asking for because the global pass slot belonged to
  // another deck for too long (capacity-busy past MAX_DEFERS). The reducer has
  // dropped its ids — turn them into per-tile `stuck` so each grey tile offers
  // its refresh control instead of hanging silently, then acknowledge so the
  // report does not re-fire.
  useEffect(() => {
    const starved = state.starved;
    if (!starved?.length) return;
    setStuck((cur) => {
      let next = cur;
      for (const { slug, ids } of starved) {
        for (const id of ids) {
          if (!next[keyOf(slug, id)]) {
            if (next === cur) next = { ...cur };
            next[keyOf(slug, id)] = true;
          }
        }
      }
      return next;
    });
    dispatch({ type: 'starved-consumed' });
  }, [state.starved]);

  const coming = useCallback(
    (slug: string, id: string) => {
      if (state.active?.slug === slug && state.active.ids.includes(id)) return true;
      return state.queue.some((j) => j.slug === slug && j.ids.includes(id));
    },
    [state.active, state.queue],
  );

  /** Did this slide end up with no image and nothing coming? Either the server
   *  gave up on it, or this hook stopped waiting. Drives the tile's refresh
   *  button — the difference between a dead grey square and a retryable one. */
  const isStuck = useCallback(
    (slug: string, id: string) => {
      if (thumbs[slug]?.[id] || known?.[slug]?.[id]) return false;
      if (coming(slug, id)) return false;
      return Boolean(stuck[keyOf(slug, id)]) || (status[slug]?.givenUp || []).includes(id);
    },
    [thumbs, known, stuck, status, coming],
  );

  /** Try one slide again, clearing every reason it might be stuck.
   *
   *  A retry that lifts only some of them is a button that appears to do
   *  nothing, so this clears all three: the server's per-slide give-up and the
   *  deck's backoff (both server-side, via the retry route) and this hook's own
   *  record. The re-render is narrowed to that slide — refreshing one tile must
   *  not re-render the deck. */
  const retry = useCallback((slug: string, id: string) => {
    // Re-queue as well as re-render. A given-up deck has left the queue, and the
    // visible SET has not changed, so nothing would otherwise dispatch again —
    // the server would render the slide and no one would be watching for it.
    setRetryNonce((n) => n + 1);
    setStuck((cur) => {
      if (!cur[keyOf(slug, id)]) return cur;
      const next = { ...cur };
      delete next[keyOf(slug, id)];
      return next;
    });
    setStatus((cur) => {
      const s = cur[slug];
      if (!s?.givenUp?.includes(id)) return cur;
      return { ...cur, [slug]: { ...s, givenUp: s.givenUp.filter((x) => x !== id), coolingUntil: null } };
    });
    fetch(`/api/decks/${slug}/thumbs/retry`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ ids: [id] }),
    })
      .then((r) => (r.ok ? (r.json() as Promise<ThumbsResponse>) : null))
      .then((res: ThumbsResponse | null) => {
        if (!res) return;
        if (res.thumbs) setThumbs((cur) => ({ ...cur, [slug]: { ...(cur[slug] || {}), ...res.thumbs } }));
      })
      .catch(() => {
        /* the tile keeps its refresh button; pressing it again is the recovery */
      });
  }, []);

  return { thumbs, status, coming, isStuck, retry };
}

/** Build a `useVisibleSet` key for one deck's slide. */
export function thumbKey(slug: string, id: string): string {
  return keyOf(slug, id);
}
